import { batch, createSignal, createUniqueId, type JSX } from 'solid-js';
import { t } from '../i18n';
import {
  ADMIN_INVITATION_FILTERS,
  type AdminInvitationFilter,
  type InvitationOutcome,
  type InvitationView,
  useAdminInvitations,
} from '../lib/admin-invitations';
import { usePagedQuery } from '../lib/paged';
import { AdminInvitationOutcome } from './AdminInvitationOutcome';
import { AdminInvitationsTable } from './AdminInvitationsTable';
import { EmptyState } from './EmptyState';
import { LoadMore } from './LoadMore';
import { PageHeader } from './PageHeader';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { Select } from './Select';
import { Skeleton } from './Skeleton';

// The admin invitations container (arc 101 slice 01). A status filter over
// `GET /v1/admin/invitations`, cursor-paginated; revoke / resend happen per row
// through the REST twins. Mounts only for staff (behind <AdminGate/>). Paging
// lives in `usePagedQuery`; this owns the filter.
//
// After a write the list is RE-READ from page one (`reload`): a revoked row
// leaves the `pending` filter, so refetching a deep cursor would append a
// shifted slice on top of what is shown, and a plain reset would answer from
// the 30s cache the write just invalidated.
//
// A write's ANSWER is held here, not in the row (`AdminInvitationOutcome`): the
// re-read can unmount the row that made it, and a resend's link is shown once.

export function AdminInvitationsPanel(props: { readonly token: string | null }): JSX.Element {
  const statusId = createUniqueId();
  const [status, setStatus] = createSignal<AdminInvitationFilter>('pending');
  const list = usePagedQuery<InvitationView>((cursor) =>
    useAdminInvitations(status, cursor, 30, props.token),
  );

  // A filter change and the reset are ONE batched write, so the query key jumps
  // straight to the new filter at `cursor=null` rather than firing a throwaway
  // fetch at the cursor the previous filter had reached.
  const [outcome, setOutcome] = createSignal<InvitationOutcome | null>(null);
  const settled = (next: InvitationOutcome): void => {
    setOutcome(next);
    list.reload();
  };

  const filter = (next: AdminInvitationFilter): void =>
    batch(() => {
      setStatus(next);
      list.reset();
    });

  return (
    <>
      <PageHeader title={t('admin.invitations_title')} desc={t('admin.invitations_subtitle')} />

      <div class="filter-row">
        <div class="admin-filter__select">
          <label id={`${statusId}-label`} for={statusId} class="sr-only">
            {t('admin.invitations_status_label')}
          </label>
          <Select
            a11y={{ id: statusId }}
            label={t('admin.invitations_status_label')}
            value={status()}
            options={ADMIN_INVITATION_FILTERS.map((s) => ({
              value: s,
              label: t(`admin.invitations_filter_${s}`),
            }))}
            onChange={filter}
          />
        </div>
      </div>

      <AdminInvitationOutcome outcome={outcome()} onDismiss={() => setOutcome(null)} />

      <Panel title={t('admin.invitations_list')}>
        <QueryBoundary
          query={list.view}
          isEmpty={(r) => r.length === 0}
          loading={
            <div class="admin__skeletons">
              <Skeleton height="3rem" />
              <Skeleton height="3rem" />
            </div>
          }
          empty={<EmptyState message={t('admin.invitations_empty')} />}
        >
          {(r) => (
            <>
              <AdminInvitationsTable rows={r} token={props.token} onOutcome={settled} />
              <LoadMore list={list} label={t('admin.load_more')} />
            </>
          )}
        </QueryBoundary>
      </Panel>
    </>
  );
}
